import { useCallback, useState } from 'react';
import { fetchAuthWrapper } from '@starjack/api';
import { urlBuilder } from '@starjack/utils';
import { useNotification } from '@starjack/features/notifications';

const templatesUrl = urlBuilder('order', '/admin/voucher-templates');

export const useVoucherTemplateFiles = () => {
    const { addNotification } = useNotification();
    const [isUploading, setIsUploading] = useState(false);

    const uploadBackground = useCallback(
        async (templateId: string, file: File): Promise<boolean> => {
            setIsUploading(true);
            try {
                // Backend expects multipart/form-data with a `file` part
                // (@RequestParam file: MultipartFile), not a raw application/pdf body.
                const formData = new FormData();
                formData.append('file', file);
                const response = await fetchAuthWrapper(
                    `${templatesUrl}/${templateId}/background`,
                    {
                        method: 'PUT',
                        body: formData,
                        headers: {
                            Accept: 'application/json',
                        },
                    }
                );
                if (!response.ok) {
                    throw new Error('Upload failed');
                }
                addNotification({
                    type: 'success',
                    message: 'Hintergrund-PDF erfolgreich hochgeladen.',
                });
                return true;
            } catch {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Hochladen. Bitte das Format prüfen (nur PDF erlaubt).',
                });
                return false;
            } finally {
                setIsUploading(false);
            }
        },
        [addNotification]
    );

    const fetchPreview = useCallback(
        async (templateId: string): Promise<string | null> => {
            try {
                const response = await fetchAuthWrapper(
                    // Cache-bust so the browser HTTP cache can't re-serve a
                    // stale preview after the template was just saved.
                    `${templatesUrl}/${templateId}/preview?t=${Date.now()}`,
                    {
                        headers: { Accept: 'image/jpeg' },
                    }
                );
                if (!response.ok) {
                    throw new Error('Preview failed');
                }
                const blob = await response.blob();
                return URL.createObjectURL(blob);
            } catch {
                addNotification({
                    type: 'error',
                    message:
                        'Vorschau konnte nicht generiert werden. Bitte erneut versuchen.',
                });
                return null;
            }
        },
        [addNotification]
    );

    const fetchBackground = useCallback(
        async (templateId: string): Promise<string | null> => {
            try {
                const response = await fetchAuthWrapper(
                    `${templatesUrl}/${templateId}/background`,
                    {
                        headers: { Accept: 'image/jpeg' },
                    }
                );
                if (!response.ok) {
                    throw new Error('Background failed');
                }
                const blob = await response.blob();
                return URL.createObjectURL(blob);
            } catch {
                return null;
            }
        },
        []
    );

    return {
        uploadBackground,
        fetchPreview,
        fetchBackground,
        isUploading,
    };
};
